import { AppRoutes } from "@/shared/lib/routes"
import { useEffect, useState } from "react"
import { Outlet, useRouter, useRouterState } from "@tanstack/react-router"

import { useAuthStore } from "@/stores/auth-store"
import { useSubscriptionVerification } from "@/features/subscriptions"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { cn } from "@/lib/utils"
import { ChatProvider, useChat } from "@/features/chat"
import { AppModalsHost } from "@/features/app-modals"
import { CreditsPurchaseModal } from "@/features/advisors"
import { ProfileModal } from "@/features/profile"
import {
  ReportPurchaseOverlay,
} from "@/features/today"
import { startIncludedReportUnlocksIfNeeded } from "@/features/today/lib/start-included-report-unlocks"
import { startOnboardingLockIfNeeded } from "@/features/onboarding-lock"
import { useSubscriptionStore } from "@/stores/subscription-store"

import { WebAppProvider } from "../providers/webapp-provider"
import { useWebApp } from "../hooks/use-webapp"
import { preloadJson } from "@/shared/lib/cached-json"
import { PalmistryTimerProvider } from "../providers/palmistry-timer-provider"
import { WebappRoutes } from "../config/routes"
import { WebappHeader } from "./webapp-header"
import { WebappNav } from "./webapp-nav"

const TITLE_BY_ROUTE: Record<string, string> = {
  [WebappRoutes.ADVISORS]: "Advisors",
  [WebappRoutes.READINGS]: "Readings",
  [WebappRoutes.TODAY]: "Today",
  [WebappRoutes.COMPATIBILITY]: "Compatibility",
  [WebappRoutes.PALMISTRY]: "Palmistry",
}

/**
 * Authenticated + subscribed chrome for the webapp: dark mobile column with a
 * fixed header and bottom navigation, wrapping the routed tab content.
 */
export function WebappShell() {
  const navigate = useNavigateWithParams()
  const authStatus = useAuthStore((s) => s.status)
  const { isVerifying, hasAccess } = useSubscriptionVerification()

  // Unauthenticated, or authenticated without an active subscription, both go
  // back to /signin — the no-subscription sheet is shown there, before the app.
  useEffect(() => {
    if (authStatus === "unauthenticated") {
      navigate({ to: AppRoutes.SIGN_IN })
    } else if (authStatus === "authenticated" && !isVerifying && !hasAccess) {
      navigate({ to: AppRoutes.SIGN_IN })
    }
  }, [authStatus, isVerifying, hasAccess, navigate])

  if (authStatus === "loading" || (authStatus === "authenticated" && isVerifying)) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-dark-base">
        <LoadingSpinner />
      </div>
    )
  }

  // Redirecting (no session, or no subscription) — render nothing meanwhile.
  if (authStatus === "unauthenticated" || !hasAccess) return null

  return (
    <WebAppProvider>
      <ChatProvider>
        <PalmistryTimerProvider>
          <WebappChrome />
        </PalmistryTimerProvider>
      </ChatProvider>
    </WebAppProvider>
  )
}

function WebappChrome() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { userMainSubscriptionId } = useChat()
  const [purchaseOpen, setPurchaseOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)

  const title = TITLE_BY_ROUTE[pathname] ?? "Advisors"

  const subscriptions = useSubscriptionStore((s) => s.subscriptions)

  // Quiz-based 30‑min lock (palmistry / compatibility) + included-plan report
  // unlocks (soulmate / astro-map at 30‑min, shorten to 10 after liked review).
  useEffect(() => {
    startOnboardingLockIfNeeded()
    startIncludedReportUnlocksIfNeeded(subscriptions)
  }, [subscriptions])

  // Close the profile sheet (and the My Plan sheet nested in it) whenever a
  // navigation resolves — e.g. tapping the Compatibility/Palmistry report row
  // links to that tab, and the sheet would otherwise stay open on top of it.
  const router = useRouter()
  useEffect(() => router.subscribe("onResolved", () => setProfileOpen(false)), [router])

  // A full-bleed view (e.g. the compatibility report) can request the header be
  // hidden via the webapp context; the routed page sets/clears the flag.
  const { headerHidden, bottomInset } = useWebApp()

  // Warm the palmistry content (small, static) once the webapp is idle so the
  // Palmistry tab opens instantly instead of flickering on first navigation.
  useEffect(() => {
    const warm = () => preloadJson("/json/palm-reading.json")
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(warm)
      return () => window.cancelIdleCallback?.(id)
    }
    const timer = window.setTimeout(warm, 1200)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    // Full-width dark backdrop so the side gutters (outside the mobile column)
    // blend with the webapp instead of showing the white body background.
    <div className="min-h-[100dvh] w-full bg-dark-base">
      <div className="relative mx-auto min-h-[100dvh] w-full max-w-app bg-dark-base text-purple-light">
        {!headerHidden && (
          <WebappHeader
            title={title}
            onCreditsClick={() => setPurchaseOpen(true)}
            onProfileClick={() => setProfileOpen(true)}
          />
        )}

        <main
          className={cn("min-h-[100dvh] px-4 pb-24", headerHidden ? "pt-0" : "pt-14")}
          // Reserve extra room under the nav for the "New message received" banner
          // (its measured height) so it never covers the page's bottom content.
          style={bottomInset ? { paddingBottom: `calc(6rem + ${bottomInset}px)` } : undefined}
        >
          <Outlet />
        </main>

        <WebappNav />

        <ReportPurchaseOverlay />

        <AppModalsHost />

        {purchaseOpen && (
          <CreditsPurchaseModal
            isOpen
            onClose={() => setPurchaseOpen(false)}
            onPurchased={() => setPurchaseOpen(false)}
            userMainSubscriptionId={userMainSubscriptionId}
            variant="main-page"
          />
        )}

        {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}
      </div>
    </div>
  )
}
